import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { EquipmentOption } from 'types/equipment.types'
import { getImageSrc } from '../../helpers/getImageSrc'
import { useDebounce } from '../../hooks/useDebounce'
import {
  EQUIPMENT_KEYS,
  getSelectReceivers,
} from '../../services/EquipmentApiService'

export type OptionType = {
  key: string | number
  value: string
  iconSrc?: string
}

type CustomAutocompleteProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  handleChangeCallback?: (value: string) => FieldValues
  handleEditValue?: (value: FieldValues) => T
  multiple?: boolean
  fullWidth: boolean
  options?: OptionType[] | string[] // статичные опции (для обратной совместимости)
  withoutTyping?: boolean
  fullDotationRegions?: string[]
  freeSolo?: boolean
  error?: boolean
  disabled?: boolean
  withUnknownValueError?: boolean
  onChange?: (value: string) => void
  withLabel?: boolean
  withClearBtn?: boolean
  fullHeight?: boolean
  onlyCustomError?: boolean
  withTooltip?: boolean
  minQueryLength?: number // минимальная длина для запроса
  setValue?: (name: string, value: string | null) => void
  setError?: (name: string, error: { message: string }) => void
  equipmentId?: string
  modelName: Path<T>
  withSetEquipmentId?: boolean
  defaultEquipmentId?: string
  setValidId?: (id: string) => void
  isReplaced?: boolean
}

const FormReceiverAutocomplete = <T extends FieldValues>(
  props: CustomAutocompleteProps<T>,
) => {
  const {
    name,
    control,
    handleChangeCallback,
    label,
    placeholder,
    options: staticOptions = [],
    handleEditValue,
    withoutTyping,
    fullDotationRegions,
    error: customError,
    withUnknownValueError,
    freeSolo,
    onChange: customOnChange,
    withLabel,
    withClearBtn,
    fullHeight,
    onlyCustomError,
    withTooltip,
    minQueryLength = 3,
    setValue,
    equipmentId,
    modelName,
    setError,
    withSetEquipmentId,
    defaultEquipmentId,
    setValidId,
    isReplaced,
    ...otherProps
  } = props

  const [trackValue, setTrackValue] = useState<EquipmentOption | null>(null)
  const [pasted, setPasted] = useState(false)
  const [unknownValueError, setUnknownValueError] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const debouncedValue = useDebounce(inputValue, 500)
  const timer = useRef<NodeJS.Timeout | null>(null)

  // Запрос через TanStack Query
  const { data: fetchedOptions = [], isFetching } = useQuery({
    queryKey: [EQUIPMENT_KEYS.GET_SELECT_RECEIVERS, name, debouncedValue],
    queryFn: () => getSelectReceivers({ sn: inputValue, size: 200 }),
    enabled: debouncedValue.length >= minQueryLength,
    staleTime: 30000,
    retry: 0,
  })

  // Выбираем откуда брать опции
  const allOptions = fetchedOptions ?? []

  const selectPaper = (options: any) => (
    <Paper {...options} className={styles.paper} />
  )

  const setModelAndSmartCard = () => {
    if (trackValue?.smartCardId && trackValue.smartCardId !== equipmentId) {
      setValue?.(
        isReplaced ? 'replacedSmartCardId' : 'smartCardId',
        trackValue.smartCardId,
      )
    } else {
      setValue?.(isReplaced ? 'replacedSmartCardId' : 'smartCardId', null)
    }

    if (trackValue) {
      setValue?.(modelName, trackValue?.model ?? '')
    } else if (Array.isArray(fetchedOptions)) {
      timer.current && clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        setError?.(name, { message: 'not found receiver' })
      }, 100)
    }
  }

  useEffect(() => {
    if (!inputValue || (inputValue && trackValue?.sn !== inputValue)) {
      !pasted && setPasted(true)
    }
  }, [fetchedOptions, inputValue])

  useEffect(() => {
    setPasted(false)
    setModelAndSmartCard()
  }, [trackValue, pasted])

  useEffect(() => {
    return () => {
      timer.current && clearTimeout(timer.current)
    }
  }, [])

  const isLoading = isFetching && inputValue.length >= minQueryLength

  const renderOption = (props: any, option: EquipmentOption, value: any) => {
    const selected = value === option

    return (
      <MenuItem
        value={option}
        className={classNames(
          styles.menuItem,
          selected && styles.menuItemSelected,
        )}
        sx={{
          h6: {
            textWrap: withLabel && !fullHeight ? 'auto' : undefined,
            textOverflow: withLabel && !fullHeight ? 'initial' : 'ellipsis',
            lineHeight: withLabel && !fullHeight ? '1.2rem' : undefined,
          },
        }}
        {...props}
      >
        <Tooltip title={option.sn} placement='top' disableHoverListener={true}>
          <Box
            width={1}
            display={'flex'}
            flexWrap={'nowrap'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography variant={'subtitle1'} sx={styles.currencyName}>
              {option.sn}
            </Typography>
          </Box>
        </Tooltip>
      </MenuItem>
    )
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
      }) => {
        if (withUnknownValueError) {
          value &&
            !allOptions.find((el) => el.sn === value) &&
            setUnknownValueError(true)
          value &&
            !!allOptions.find((el) => el.sn === value) &&
            setUnknownValueError(false)
        }

        if (value !== trackValue?.sn && value.length === 23) {
          setTrackValue(allOptions.find((el) => el.sn === value) ?? null)
        }

        return (
          <Autocomplete
            options={allOptions}
            value={value}
            multiple={false}
            onChange={(_e, newValue) => {
              onChange(newValue?.sn ?? '')
              newValue?.sn && setTrackValue(newValue)
              setInputValue(newValue?.sn ?? '')
            }}
            isOptionEqualToValue={(o) =>
              o.sn === trackValue?.sn && o.model === trackValue?.model
            }
            disabled={otherProps.disabled}
            PaperComponent={selectPaper}
            noOptionsText={isLoading ? 'Загрузка...' : 'Нет совпадений'}
            renderOption={(props, option) => renderOption(props, option, value)}
            clearIcon={null}
            inputValue={inputValue}
            onInputChange={(_e, value) => {
              const clearValue = value.replace(/\D/g, '')
              setInputValue(clearValue ?? '')
              onChange(clearValue)
              if (clearValue.length === 23 && clearValue !== trackValue?.sn) {
                const o = allOptions.find((el) => el.sn === clearValue)

                setTrackValue(o ?? null)
              }
            }}
            filterOptions={(x) => x} // не фильтруем локально
            loading={isLoading}
            popupIcon={
              isLoading ? (
                <CircularProgress size={23} sx={{ mr: '7px' }} />
              ) : (
                <img
                  alt={''}
                  src={getImageSrc('downIcon')}
                  style={{
                    height: '23px',
                    width: '23px',
                    marginRight: '7px',
                    opacity: otherProps.disabled ? '.3' : '.8',
                  }}
                />
              )
            }
            renderInput={(params) => {
              return (
                <>
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    label={label}
                    error={
                      onlyCustomError
                        ? customError
                        : !!error?.message || customError || unknownValueError
                    }
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: withoutTyping,
                      maxLength: 23,
                    }}
                    sx={{
                      '.MuiFormControl-root': {
                        position: 'relative',
                      },

                      input: {
                        padding: '0 4px 0 6px!important',
                        cursor: withoutTyping ? 'pointer' : 'text',
                        overflow: 'hidden!important',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis !important',
                        maxWidth: '100%',
                        width: '90%!important',
                      },
                    }}
                    {...field}
                    {...otherProps}
                  />

                  {!!value && (
                    <IconButton
                      onClick={() => {
                        onChange('')
                        setInputValue('')
                        setTrackValue(null)
                        setModelAndSmartCard()
                      }}
                      sx={{
                        position: 'absolute',
                        top: '6px',
                        right: '40px',
                        padding: '2px',
                      }}
                    >
                      <img
                        src={getImageSrc('close')}
                        alt={'Стереть'}
                        style={{
                          width: '24px',
                        }}
                      />
                    </IconButton>
                  )}
                </>
              )
            }}
            sx={{ position: 'relative' }}
          />
        )
      }}
    />
  )
}

export const styles = {
  autocompleteField: {
    borderRadius: 8,
  },
  autocompleteFieldInput: {
    padding: '0 4px !important',
  },
  popupIndicator: {
    padding: 0,
    marginRight: '16px',
    '&:hover': {
      background: 'transparent',
    },
  },
  autocompleteListbox: {
    padding: 0,
  },
  currencyName: {
    fontSize: '1rem',
    lineHeight: '28px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '300px',
    fontWeight: '400 !important',
  },
  menuItem: {
    borderRadius: '8px',
    width: '100%',
    background: '#FFF',
  },
  menuItemSelected: {
    background: '#EBF4FF !important',
  },
  option: {
    borderRadius: '8px',
    backgroundColor: '#F6ECDF !important',
    padding: 0,
    margin: '2px 0',
  },
  paper: {
    padding: '12px',
    margin: '6px 0',
    boxShadow: (props: any) =>
      `0 0 0 2px ${
        props.borderColor ? props.borderColor : '#A1A1A1'
      }, 0px 12px 16px -8px rgba(52, 52, 52, 0.12)`,
  },
}

export default observer(FormReceiverAutocomplete)
